import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Users,
  Calendar,
  DollarSign,
  Briefcase,
  MessageCircle,
  Edit,
  Plus,
  Mail,
  Phone,
  Building,
  MapPin,
  Clock,
  FileText,
  Shield,
  Minus,
  Copy,
  Check
} from 'lucide-react';
import { clientService, caseService, calendarService } from '../../services/api';
import CreateCaseModal from '../../components/CreateCaseModal';
import CaseCommunicationsSection from '../../components/communications/CaseCommunicationsSection';
import CreateInvoiceModal from '../../components/invoices/CreateInvoiceModal';
import RecordTrustPaymentModal from '../../components/trust/RecordTrustPaymentModal';
import WithdrawFromTrustModal from '../../components/trust/WithdrawFromTrustModal';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';
import ClientNotesSection from '../../components/client-notes/ClientNotesSection';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [clientData, setClientData] = useState(null);
  const [clientCases, setClientCases] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Modal states
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [smsConsentLoading, setSmsConsentLoading] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = useCallback((text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const fetchClientData = useCallback(async () => {
    try {
      const response = await clientService.getClient(id);
      setClientData(response.data);
      setEditForm(response.data);
    } catch (error) {
      console.error('Error fetching client:', error);
      setError('Failed to load client details');
    }
  }, [id]);

  const fetchClientCases = useCallback(async () => {
    try {
      const response = await caseService.getCasesByClient(id);
      setClientCases(response.data || []);
    } catch (error) {
      console.error('Error fetching client cases:', error);
    }
  }, [id]);

  const fetchUpcomingEvents = useCallback(async () => {
    try {
      if (clientCases.length === 0) return;

      // Get events for all client cases
      const eventPromises = clientCases.map(async (caseItem) => {
        try {
          const now = new Date();
          const futureDate = new Date();
          futureDate.setMonth(futureDate.getMonth() + 3); // Next 3 months

          const response = await calendarService.getEvents({
            case_id: caseItem.id,
            start_date: now.toISOString().split('T')[0],
            end_date: futureDate.toISOString().split('T')[0]
          });

          return (response.data?.events || []).map(event => ({
            ...event,
            case_title: caseItem.title,
            case_number: caseItem.case_number
          }));
        } catch (error) {
          console.error(`Error fetching events for case ${caseItem.id}:`, error);
          return [];
        }
      });

      const eventArrays = await Promise.all(eventPromises);
      const allEvents = eventArrays.flat();

      // Sort by start time and take next 5 events
      const sortedEvents = allEvents
        .filter(event => new Date(event.start_time) > new Date())
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
        .slice(0, 5);

      setUpcomingEvents(sortedEvents);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    }
  }, [clientCases]);

  const fetchBillingSummary = useCallback(async () => {
    try {
      const response = await clientService.getBillingSummary(id);
      setBillingSummary(response.data);
    } catch (error) {
      console.error('Error fetching billing summary:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchClientData();
    fetchClientCases();
    fetchBillingSummary();
    setLoading(false);
  }, [fetchClientData, fetchClientCases, fetchBillingSummary]);

  useEffect(() => {
    if (clientCases.length > 0) {
      fetchUpcomingEvents();
    }
  }, [clientCases, fetchUpcomingEvents]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await clientService.updateClient(id, editForm);
      setClientData(editForm);
      setIsEditing(false);
      showSuccess('Client updated successfully!');
    } catch (error) {
      console.error('Error updating client:', error);
      showError('Failed to update client. Please try again.');
    }
  };

  const handleEditCancel = () => {
    setEditForm(clientData);
    setIsEditing(false);
  };

  // Modal handlers
  const handleCaseCreated = (newCase) => {
    setShowCreateCaseModal(false);
    fetchClientCases(); // Refresh cases list
    if (newCase && newCase.id) {
      navigate(`/cases/${newCase.id}`);
    }
  };

  const handleInvoiceCreated = (newInvoice) => {
    setShowCreateInvoiceModal(false);
    fetchBillingSummary();
    if (newInvoice && newInvoice.id) {
      navigate(`/invoices/${newInvoice.id}`);
    }
  };

  const handlePaymentRecorded = () => {
    setShowRecordPaymentModal(false);
    fetchClientData(); // Refresh to show updated trust balance
    showSuccess('Deposit recorded to trust account');
  };

  const handleWithdrawalRecorded = () => {
    setShowWithdrawModal(false);
    fetchClientData(); // Refresh to show updated trust balance
    showSuccess('Withdrawal recorded from trust account');
  };


  const handleRequestSmsConsent = async () => {
    if (!clientData?.sms_phone && !clientData?.phone) {
      showError('Client must have a phone number to request SMS consent.');
      return;
    }
    setSmsConsentLoading(true);
    try {
      await clientService.requestSmsConsent(id);
      showSuccess('SMS consent request sent');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send SMS consent request.';
      showError(msg);
    } finally {
      setSmsConsentLoading(false);
    }
  };

  const getCaseStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'intake':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      case 'on_hold':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatEventType = (type) => {
    switch (type) {
      case 'meeting':
        return { icon: Users, color: 'text-yellow-600', label: 'Meeting' };
      case 'court_date':
        return { icon: Briefcase, color: 'text-red-600', label: 'Court Date' };
      case 'deadline':
        return { icon: Clock, color: 'text-yellow-600', label: 'Deadline' };
      case 'call':
        return { icon: Phone, color: 'text-green-600', label: 'Call' };
      case 'appointment':
        return { icon: Calendar, color: 'text-purple-600', label: 'Appointment' };
      case 'consultation':
        return { icon: Users, color: 'text-indigo-600', label: 'Consultation' };
      default:
        return { icon: Calendar, color: 'text-gray-600', label: 'Event' };
    }
  };

  const formatEventTime = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();

    const isToday = start.toDateString() === now.toDateString();
    const isTomorrow = start.toDateString() === new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString();

    let dateStr;
    if (isToday) {
      dateStr = 'Today';
    } else if (isTomorrow) {
      dateStr = 'Tomorrow';
    } else {
      dateStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    const timeStr = start.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `${dateStr} at ${timeStr}`;
  };

  const handleEventClick = (event) => {
    // Navigate to calendar focused on this event's date
    const eventDate = new Date(event.start_time).toISOString().split('T')[0];
    navigate(`/calendar?date=${eventDate}&event=${event.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error || !clientData) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <Users className="h-16 w-16 mx-auto" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Client Not Found</h2>
        <p className="text-gray-600 mb-4">{error || 'The requested client could not be found.'}</p>
        <Link
          to="/clients"
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
        >
          Back to Clients
        </Link>
      </div>
    );
  }

  const clientName = clientData.company_name || `${clientData.first_name} ${clientData.last_name}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                {clientData.client_type === 'business' ? (
                  <Building className="mr-3 h-8 w-8" />
                ) : (
                  <Users className="mr-3 h-8 w-8" />
                )}
                {clientName}
              </h1>
              <span className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-800 capitalize">
                {clientData.client_type}
              </span>
              <span className={`px-3 py-1 text-sm rounded-full ${
                clientData.status === 'active' ? 'bg-green-100 text-green-800' :
                clientData.status === 'closed' ? 'bg-red-100 text-red-800' :
                clientData.status === 'intake' ? 'bg-blue-100 text-blue-800' :
                clientData.status === 'not_hired' ? 'bg-gray-200 text-gray-600' :
                'bg-gray-100 text-gray-800'
              }`}>
                {clientData.status === 'active' ? 'Active' :
                 clientData.status === 'closed' ? 'Closed' :
                 clientData.status === 'not_hired' ? 'Not Hired' :
                 clientData.status === 'intake' ? 'Intake' : clientData.status}
              </span>
            </div>
            <p className="text-gray-600">
              Client since {new Date(clientData.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsEditing(true)}
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600"
        >
          <Edit className="mr-2 h-4 w-4" />
          Edit Client
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Client Information</h2>

            {isEditing ? (
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {clientData.client_type === 'individual' ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={editForm.first_name || ''}
                          onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={editForm.last_name || ''}
                          onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={editForm.company_name || ''}
                        onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editForm.email || ''}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={editForm.phone || ''}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                </div>

                {/* Address Fields */}
                <div className="space-y-4 border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900">Address</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={editForm.address_line1 || ''}
                      onChange={(e) => setEditForm({...editForm, address_line1: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Street address, P.O. box, company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={editForm.address_line2 || ''}
                      onChange={(e) => setEditForm({...editForm, address_line2: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Apartment, suite, unit, building, floor (optional)"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={editForm.city || ''}
                        onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State / Province / Region
                      </label>
                      <input
                        type="text"
                        value={editForm.state_province || ''}
                        onChange={(e) => setEditForm({...editForm, state_province: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP / Postal Code
                      </label>
                      <input
                        type="text"
                        value={editForm.postal_code || ''}
                        onChange={(e) => setEditForm({...editForm, postal_code: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <input
                        type="text"
                        value={editForm.country || ''}
                        onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={editForm.notes || ''}
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>

                {/* Client Status */}
                <div className="space-y-4 border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900">Client Status</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={editForm.status || 'intake'}
                      onChange={(e) => {
                        const newStatus = e.target.value;
                        setEditForm({
                          ...editForm,
                          status: newStatus,
                          ...(newStatus !== 'not_hired' ? { not_hired_reason: null } : {})
                        });
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="active">Active</option>
                      <option value="closed">Closed</option>
                      <option value="intake">Intake</option>
                      <option value="not_hired">Not Hired</option>
                    </select>
                  </div>

                  {editForm.status === 'not_hired' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reason Not Hired</label>
                      <textarea
                        value={editForm.not_hired_reason || ''}
                        onChange={(e) => setEditForm({...editForm, not_hired_reason: e.target.value || null})}
                        rows={2}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Optional reason for not hiring this client"
                      />
                    </div>
                  )}
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={handleEditCancel}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {clientData.email && (
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-500">Email</label>
                        <div className="flex items-center space-x-2">
                          <a href={`mailto:${clientData.email}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                            {clientData.email}
                          </a>
                          <button
                            onClick={() => copyToClipboard(clientData.email, 'email')}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            title="Copy email"
                          >
                            {copiedField === 'email' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {clientData.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone</label>
                        <div className="flex items-center space-x-2">
                          <a href={`tel:${clientData.phone}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                            {clientData.phone}
                          </a>
                          <button
                            onClick={() => copyToClipboard(clientData.phone, 'phone')}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            title="Copy phone number"
                          >
                            {copiedField === 'phone' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {(clientData.address_line1 || clientData.city || clientData.state_province || clientData.postal_code || clientData.country) && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Address</label>
                      <div className="flex items-start space-x-2">
                        <p className="text-gray-900">
                          {clientData.address_line1 && <span>{clientData.address_line1}<br /></span>}
                          {clientData.address_line2 && <span>{clientData.address_line2}<br /></span>}
                          {(clientData.city || clientData.state_province || clientData.postal_code) && (
                            <span>
                              {clientData.city && clientData.city}
                              {clientData.city && clientData.state_province && ', '}
                              {clientData.state_province && clientData.state_province}
                              {clientData.postal_code && ' ' + clientData.postal_code}
                              <br />
                            </span>
                          )}
                          {clientData.country && <span>{clientData.country}</span>}
                        </p>
                        <button
                          onClick={() => {
                            const parts = [
                              clientData.address_line1,
                              clientData.address_line2,
                              [clientData.city, clientData.state_province].filter(Boolean).join(', ') + (clientData.postal_code ? ' ' + clientData.postal_code : ''),
                              clientData.country
                            ].filter(Boolean).join('\n');
                            copyToClipboard(parts, 'address');
                          }}
                          className="text-gray-400 hover:text-gray-600 transition-colors mt-0.5"
                          title="Copy address"
                        >
                          {copiedField === 'address' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {clientData.notes && (
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Notes</label>
                      <p className="text-gray-900 whitespace-pre-line">{clientData.notes}</p>
                    </div>
                  </div>
                )}

                {clientData.status === 'not_hired' && clientData.not_hired_reason && (
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Not Hired Reason</label>
                      <p className="text-gray-900 whitespace-pre-line">{clientData.not_hired_reason}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cases */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Cases</h2>
              <button
                onClick={() => setShowCreateCaseModal(true)}
                className="bg-green-600 text-white px-3 py-1 rounded text-sm flex items-center hover:bg-green-700"
              >
                <Plus className="mr-1 h-4 w-4" />
                New Case
              </button>
            </div>

            {clientCases.length === 0 ? (
              <div className="text-center py-8">
                <Briefcase className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600">No cases yet</p>
                <p className="text-gray-500 text-sm">Create the first case for this client</p>
              </div>
            ) : (
              <div className="space-y-3">
                {clientCases.map((caseItem) => (
                  <div
                    key={caseItem.id}
                    onClick={() => navigate(`/cases/${caseItem.id}`)}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-blue-300 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{caseItem.title}</h3>
                        <p className="text-sm text-gray-600">Case #{caseItem.case_number}</p>
                        <p className="text-sm text-gray-500 capitalize">{caseItem.case_type}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs rounded-full ${getCaseStatusColor(caseItem.status)}`}>
                          {caseItem.status}
                        </span>
                        <p className="text-xs text-gray-500 mt-1 flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {new Date(caseItem.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Client Notes */}
          <ClientNotesSection
            clientId={id}
            onSuccess={showSuccess}
            onError={showError}
          />

          {/* Recent Communications */}
          <CaseCommunicationsSection clientId={id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Trust Account Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Shield className="mr-2 h-5 w-5" />
              Trust Account
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Current Balance</span>
                <span className="font-semibold text-green-600">
                  ${clientData.trust_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </span>
              </div>
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between mb-2">
                  <Link
                    to={`/trust-accounts?client=${id}`}
                    className="text-yellow-600 hover:text-gray-800 text-sm"
                  >
                    View Trust Details →
                  </Link>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowRecordPaymentModal(true)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Deposit
                  </button>
                  <button
                    onClick={() => setShowWithdrawModal(true)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Minus className="h-3 w-3 mr-1" />
                    Withdraw
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Billing Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <DollarSign className="mr-2 h-5 w-5" />
              Billing Summary
            </h3>
            {billingSummary ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Invoiced</span>
                  <span className="font-semibold">
                    ${billingSummary.total_invoiced.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Paid</span>
                  <span className="font-semibold text-green-600">
                    ${billingSummary.total_paid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Outstanding</span>
                  <span className={`font-semibold ${billingSummary.total_outstanding > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    ${billingSummary.total_outstanding.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="pt-2 border-t">
                  <Link
                    to={`/invoices?client=${id}`}
                    className="text-yellow-600 hover:text-gray-800 text-sm"
                  >
                    View Invoices ({billingSummary.invoice_count}) →
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-sm">Loading...</div>
            )}
          </div>

          {/* Upcoming Events */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Calendar className="mr-2 h-5 w-5" />
                Upcoming Events
              </h3>
              <Link
                to={`/calendar?client=${id}`}
                className="text-yellow-600 hover:text-gray-800 text-sm"
              >
                View All
              </Link>
            </div>

            {upcomingEvents.length === 0 ? (
              <div className="text-center py-4">
                <Calendar className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600 text-sm">No upcoming events</p>
                <p className="text-gray-500 text-xs">Schedule a meeting to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event) => {
                  const eventFormat = formatEventType(event.event_type);
                  const Icon = eventFormat.icon;

                  return (
                    <div
                      key={event.id}
                      onClick={() => handleEventClick(event)}
                      className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 hover:border-blue-300 cursor-pointer transition-colors"
                      title="Click to view event in calendar"
                    >
                      <div className="flex items-start space-x-3">
                        <Icon className={`h-4 w-4 ${eventFormat.color} mt-1`} />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm truncate">
                            {event.title}
                          </h4>
                          <p className="text-xs text-gray-500 mb-1">
                            {event.case_title}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">
                              {formatEventTime(event.start_time, event.end_time)}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full bg-gray-100 ${eventFormat.color}`}>
                              {eventFormat.label}
                            </span>
                          </div>
                          {event.location && (
                            <p className="text-xs text-gray-500 mt-1 flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />
                              {event.location}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Client Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Client Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Cases</span>
                <span className="font-medium">{clientCases.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Active Cases</span>
                <span className="font-medium">
                  {clientCases.filter(c => c.status === 'active').length}
                </span>
              </div>
            </div>
          </div>

          {/* SMS Consent Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <MessageCircle className="mr-2 h-5 w-5" />
              SMS Consent
            </h3>
            {clientData.sms_consent ? (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="text-sm font-medium text-green-800">Consent Granted</span>
                </div>
                {clientData.sms_consent_date && (
                  <p className="text-xs text-gray-500">
                    Since {new Date(clientData.sms_consent_date).toLocaleDateString()}
                  </p>
                )}
                {clientData.sms_phone && (
                  <p className="text-sm text-gray-700">SMS Phone: {clientData.sms_phone}</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-400"></span>
                  <span className="text-sm text-gray-600">Not yet granted</span>
                </div>
                {clientData.sms_phone && (
                  <p className="text-sm text-gray-700">SMS Phone: {clientData.sms_phone}</p>
                )}
                <button
                  onClick={handleRequestSmsConsent}
                  disabled={smsConsentLoading}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
                >
                  {smsConsentLoading ? 'Sending...' : 'Request SMS Consent'}
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Modals */}
      {showCreateCaseModal && (
        <CreateCaseModal
          isOpen={showCreateCaseModal}
          onClose={() => setShowCreateCaseModal(false)}
          onSuccess={handleCaseCreated}
          preSelectedClientId={id}
        />
      )}

      {showCreateInvoiceModal && (
        <CreateInvoiceModal
          onClose={() => setShowCreateInvoiceModal(false)}
          onSuccess={handleInvoiceCreated}
          initialClientId={id}
          initialClientName={clientName}
        />
      )}

      {showRecordPaymentModal && (
        <RecordTrustPaymentModal
          isOpen={showRecordPaymentModal}
          onClose={() => setShowRecordPaymentModal(false)}
          clientId={id}
          clientName={clientName}
          onPaymentRecorded={handlePaymentRecorded}
        />
      )}

      {showWithdrawModal && (
        <WithdrawFromTrustModal
          isOpen={showWithdrawModal}
          onClose={() => setShowWithdrawModal(false)}
          clientId={id}
          clientName={clientName}
          trustBalance={clientData?.trust_balance || 0}
          onWithdrawalRecorded={handleWithdrawalRecorded}
        />
      )}

      {/* Notification Toast */}
      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default ClientDetail;